import { env } from '@/env'

interface FbqFn {
  (...args: unknown[]): void
  callMethod?: (...args: unknown[]) => void
  queue?: unknown[]
  loaded?: boolean
  version?: string
}

declare global {
  interface Window {
    fbq?: FbqFn
    _fbq?: FbqFn
  }
}

type EventParams = Record<string, unknown>

const PIXEL_ID = env.VITE_FB_PIXEL_ID

// Só dispara no build de produção e com o ID configurado — assim dev/staging não
// poluem os dados do pixel nem a otimização dos anúncios.
const enabled = import.meta.env.PROD && !!PIXEL_ID

// Reimplementação tipada do snippet base do Facebook: cria o fbq que enfileira
// chamadas até o fbevents.js carregar, e injeta o script.
function loadPixel(): void {
  if (window.fbq) return

  const fbq: FbqFn = (...args: unknown[]) => {
    if (fbq.callMethod) fbq.callMethod(...args)
    else fbq.queue?.push(args)
  }
  fbq.queue = []
  fbq.loaded = true
  fbq.version = '2.0'
  window.fbq = fbq
  window._fbq ??= fbq

  const script = document.createElement('script')
  script.async = true
  script.src = 'https://connect.facebook.net/en_US/fbevents.js'
  document.head.appendChild(script)
}

export function initFbPixel(): void {
  if (!enabled) return
  loadPixel()
  window.fbq?.('init', PIXEL_ID)
  window.fbq?.('track', 'PageView')
}

function track(event: string, params?: EventParams, options?: { eventID: string }): void {
  if (!enabled || !window.fbq) return
  if (options) window.fbq('track', event, params ?? {}, options)
  else window.fbq('track', event, params ?? {})
}

export function trackPageView(): void {
  track('PageView')
}

export function trackInitiateCheckout(input: { plan: string; value?: number }): void {
  track('InitiateCheckout', {
    content_category: input.plan,
    ...(input.value ? { value: input.value, currency: 'BRL' } : {}),
  })
}

export function trackLead(input: { plan: string; value?: number }): void {
  track('Lead', {
    content_category: input.plan,
    ...(input.value ? { value: input.value, currency: 'BRL' } : {}),
  })
}

// eventId = id do pagamento → deduplicação no Facebook (e pronto pra Conversions API depois).
export function trackPurchase(input: { value: number; currency: string; eventId: string }): void {
  track('Purchase', { value: input.value, currency: input.currency }, { eventID: input.eventId })
}
